<template>
  <AppDialog
    v-model="isVisible"
    modal
    :closable="true"
    custom-class="transfer-chat-dialog backdrop-blur-xl! relative px-4"
    width="min(1100px, calc(100vw - 2rem))"
    :breakpoints="{ '640px': 'calc(100vw - 1rem)' }"
  >
    <template #header>
      <div
        v-if="!showPendingAgentTransfer"
        class="mt-5 flex w-full flex-col gap-3 pr-8 pb-2 sm:flex-row sm:items-start sm:gap-4 sm:pr-10"
      >
        <div class="flex w-full min-w-0 flex-col gap-3 sm:gap-4">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
            <div class="flex min-w-0 flex-col">
              <div class="flex flex-row gap-2">
                <div
                  class="flex items-center w-fit shrink-0 rounded-md bg-[var(--p-primary-color)]/20 p-1.5"
                >
                  <IconChatTransfer width="30" height="30" class="text-[var(--p-primary-color)]  dark:text-[var(--p-surface-0)]" />
                </div>
                <div class="flex flex-col">
                  <label
                    class="montserrat break-words text-xl font-semibold leading-tight text-[var(--p-primary-color)]  dark:text-[var(--p-surface-0)]"
                  >
                    {{ t('whatsapp.transfer_chat') }}
                  </label>
                  <label
                    class="mt-0 text-xs leading-snug text-[var(--p-surface-600)] dark:text-[var(--p-surface-100)]"
                  >
                    {{ t('whatsapp.transfer_chat_description') }}
                  </label>
                </div>
              </div>
            </div>

            <div
              class="flex min-w-0 flex-1 items-center justify-start gap-1 sm:flex-none sm:justify-end gap-5"
            >
              <div class="flex min-w-0 items-center justify-center gap-1 sm:justify-start sm:gap-2">
                <div
                  class="flex h-8 w-8 shrink-0 shadow-sm items-center justify-center rounded-full bg-[var(--p-primary-color)] text-sm font-semibold text-white"
                >
                  1
                </div>
                <label
                  class="montserrat sr-only truncate text-sm font-semibold text-[var(--p-primary-color)] dark:text-[var(--p-text-color)] sm:not-sr-only lg:block"
                >
                  {{ t('general.destination_type') }}
                </label>
              </div>

              <div class="flex min-w-0 items-center justify-center gap-1 sm:justify-start sm:gap-2">
                <div
                  class="flex h-8 w-8 shrink-0 shadow-sm items-center justify-center rounded-full text-sm font-semibold text-white"
                  :class="
                    transferType
                      ? 'bg-[var(--p-primary-color)]'
                      : 'bg-[var(--p-surface-400)] dark:bg-[var(--p-surface-600)]'
                  "
                >
                  2
                </div>
                <label
                  class="montserrat sr-only truncate text-sm font-semibold sm:not-sr-only lg:block"
                  :class="
                    transferType
                      ? 'text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]'
                      : 'text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]'
                  "
                >
                  {{ t('general.destination') }}
                </label>
              </div>

              <div class="flex min-w-0 items-center justify-center gap-1 sm:justify-start sm:gap-2">
                <div
                  class="flex h-8 w-8 shrink-0 shadow-sm items-center justify-center rounded-full text-sm font-semibold text-white"
                  :class="
                    showSummary
                      ? 'bg-[var(--p-primary-color)]'
                      : 'bg-[var(--p-surface-400)] dark:bg-[var(--p-surface-600)]'
                  "
                >
                  3
                </div>
                <label
                  class="montserrat sr-only truncate text-sm font-semibold sm:not-sr-only lg:block"
                  :class="
                    showSummary
                      ? 'text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]'
                      : 'text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]'
                  "
                >
                  {{ t('general.confirmation') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="dark:text-[var(--p-text-color)]">
      <div class="flex flex-col gap-6">
        <Message v-if="loadError" severity="error" :closable="false" size="small">
          {{ loadError }}
        </Message>

        <TransferTypeSelector
          v-if="!transferType"
          :model-value="transferType"
          @update:model-value="handleTransferTypeChange"
        />

        <TransferCampaignSelector
          v-if="transferType === 'campaign' && !showSummary"
          v-model="selectedCampaign"
          :campaigns="filteredCampaigns"
          :search="campaignSearchTerm"
          :loading="isLoadingCampaigns"
          :title="t('whatsapp.select_campaing')"
          search-placeholder="Busca una campaña..."
          :empty-text="campaignDirectoryEmptyText"
          @back="handleBackStep"
          @update:search="campaignSearchTerm = $event"
        />

        <TransferUserSelector
          v-if="transferType === 'user' && !showSummary"
          v-model="selectedUser"
          :users="filteredTransferUsers"
          :campaigns="availableCampaigns"
          :selected-campaign="selectedUserCampaign"
          :search="userSearchTerm"
          :loading="isLoadingUserDirectory"
          :title="t('whatsapp.select_agent')"
          :search-placeholder="t('general.search_agent_camaping')"
          :campaign-placeholder="$t('general.all_campaings')"
          :empty-text="userDirectoryEmptyText"
          @back="handleBackStep"
          @update:selected-campaign="selectedUserCampaign = $event"
          @update:search="userSearchTerm = $event"
        />

        <TransferBotSelector
          v-if="transferType === 'bot' && !showSummary"
          v-model="selectedBot"
          :bots="filteredBots"
          :search="botSearchTerm"
          :loading="isLoadingBots"
          :title="t('whatsapp.select_bot')"
          search-placeholder="Busca un bot..."
          :empty-text="botDirectoryEmptyText"
          @back="handleBackStep"
          @update:search="botSearchTerm = $event"
        />

        <TransferPendingAgentStep
          v-if="showPendingAgentTransfer"
          :contact-name="contactName"
          :contact-phone="contactPhone"
          :destination-name="summaryDestinationName"
          :destination-subtitle="summaryDestinationSubtitle"
          :destination-meta="summaryDestinationMeta"
          :countdown="transferResponseCountdown"
        />

        <TransferSummaryStep
          v-else-if="showSummary"
          :contact-name="contactName"
          :contact-phone="contactPhone"
          :transfer-type="summaryTransferType"
          :destination-name="summaryDestinationName"
          :destination-subtitle="summaryDestinationSubtitle"
          :destination-meta="summaryDestinationMeta"
          :show-message-field="transferType === 'user'"
          :message="transferMessage"
          :message-disabled="!selectedUser"
          @back="handleBackStep"
          @update:message="transferMessage = $event"
        />
      </div>
    </div>

    <template #footer>
      <div v-if="showFooterActions" class="flex flex-row w-full justify-center items-center gap-5">
        <AppButtonIcon
          label="Cancelar"
          severity="secondary"
          variant="text"
          size="small"
          class="montserrat whitespace-nowrap w-full"
          :disabled="isSubmittingTransfer"
          @click="handleClose"
        />

        <AppButtonIcon
          :label="t('whatsapp.chat_transfer')"
          size="small"
          severity="primary"
          :loading="isSubmittingTransfer"
          class="montserrat whitespace-nowrap w-full transition-colors duration-200 hover:opacity-90"
          :disabled="!canTransfer || isSubmittingTransfer"
          @click="handleTransfer"
          iconPos="right"
        >
          <template #icon>
            <IconTransfer width="14" height="14" />
          </template>
        </AppButtonIcon>
      </div>
    </template>
  </AppDialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import Message from 'primevue/message'
import type { CampaignOption } from '@/components/whatsapp/composables/useTransferDialog'
import { useTransferDialog } from '@/components/whatsapp/composables/useTransferDialog'
import type { BotOption } from '@/services/bot/interfaces'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useBotService } from '@/services/bot/useBotService'
import { ChatChannel } from '@/services/campaign/enums/chat-channel.enum'
import IconChatTransfer from '@/assets/svg/transfer_chat_icon.svg?component'
import TransferBotSelector from './steps-transfers/TransferBotSelector.vue'
import TransferCampaignSelector from './steps-transfers/TransferCampaignSelector.vue'
import TransferSummaryStep from './steps-transfers/TransferSummaryStep.vue'
import TransferPendingAgentStep from './steps-transfers/TransferPendingAgentStep.vue'
import TransferTypeSelector from './steps-transfers/TransferTypeSelector.vue'
import TransferUserSelector from './steps-transfers/TransferUserSelector.vue'
import type { TransferUserCardOption } from './steps-transfers/TransferUserSelector.vue'
import IconTransfer from '@/assets/svg/transfer_icon.svg?component'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

const { t } = useI18n()

interface Props {
  visible: boolean
  chatId: string
  contactName: string
  contactPhone: string
  currentCampaignId: string
  currentCampaignName: string
  // Canal del chat (whatsapp / webchat): solo se ofrecen bots de ese canal.
  channelId?: string
  currentUserId: string
  currentUserName: string
  isAwaitingTransferResponse?: boolean
  restoreDraft?: {
    nonce: number
    action: 'type' | 'retry'
    transferData?: {
      chatId: string
      transferType: 'user'
      toCampaignId: string
      toCampaignName: string
      toUserId: string
      toUserName: string
      transfer_message?: string
    } | null
  } | null
}

type TransferPayload =
  | {
      chatId: string
      transferType: 'campaign'
      toCampaignId: string
      toCampaignName: string
    }
  | {
      chatId: string
      transferType: 'user'
      toCampaignId: string
      toCampaignName: string
      toUserId: string
      toUserName: string
      transfer_message?: string
    }
  | {
      chatId: string
      transferType: 'bot'
      toBotId: string
      toBotName: string
    }

const props = withDefaults(defineProps<Props>(), {
  restoreDraft: null,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  transfer: [data: TransferPayload]
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

const {
  campaigns: availableCampaignsData,
  isLoadingCampaigns,
  error: loadError,
  loadWhatsAppCampaigns,
  reset: resetTransferData,
} = useTransferDialog()

const botService = useBotService()
const campaignService = useCampaignService()

const transferType = ref<'campaign' | 'user' | 'bot' | null>(null)
const selectedCampaign = ref<CampaignOption | null>(null)
const selectedUserCampaign = ref<CampaignOption | null>(null)
const selectedUser = ref<TransferUserCardOption | null>(null)
const selectedBot = ref<BotOption | null>(null)
const transferMessage = ref('')
const isLoading = ref(false)
const isLoadingBots = ref(false)
const availableBots = ref<BotOption[]>([])
const userSearchTerm = ref('')
const campaignSearchTerm = ref('')
const botSearchTerm = ref('')
const isLoadingUserDirectory = ref(false)
const transferUsersDirectory = ref<TransferUserCardOption[]>([])
const transferResponseCountdown = ref(20)
let transferResponseCountdownInterval: ReturnType<typeof setInterval> | null = null
const isRestoringDraft = ref(false)
const lastAppliedRestoreNonce = ref<number | null>(null)

const availableCampaigns = computed(() => availableCampaignsData.value)

// Campañas, agentes y bots de destino son los del canal del chat.
const chatChannel = computed(() =>
  props.channelId === 'webchat' ? ChatChannel.WEBCHAT : ChatChannel.WHATSAPP
)

const isSubmittingTransfer = computed(() => {
  return isLoading.value || !!props.isAwaitingTransferResponse
})

const isClosingDisabled = computed(() => {
  return isLoading.value
})

const filteredTransferUsers = computed(() => {
  const term = userSearchTerm.value.trim().toLowerCase()

  return transferUsersDirectory.value.filter((user) => {
    const matchesCampaign = selectedUserCampaign.value
      ? user.campaignId === selectedUserCampaign.value.id
      : true

    const matchesTerm = term
      ? user.name.toLowerCase().includes(term) ||
        (user.alias ?? '').toLowerCase().includes(term) ||
        user.campaignName.toLowerCase().includes(term)
      : true

    return matchesCampaign && matchesTerm
  })
})

const filteredCampaigns = computed(() => {
  const term = campaignSearchTerm.value.trim().toLowerCase()

  return availableCampaigns.value.filter((campaign) => {
    if (!term) return true
    return campaign.name.toLowerCase().includes(term)
  })
})

const filteredBots = computed(() => {
  const term = botSearchTerm.value.trim().toLowerCase()

  return availableBots.value.filter((bot) => {
    if (!term) return true
    return (
      bot.name.toLowerCase().includes(term) ||
      bot.id.toLowerCase().includes(term) ||
      bot.description?.toLowerCase().includes(term)
    )
  })
})

const userDirectoryEmptyText = computed(() => {
  if (isLoadingUserDirectory.value) return ''
  if (!transferUsersDirectory.value.length) return 'No hay agentes disponibles para transferir.'
  return 'No encontramos agentes con los filtros actuales.'
})

const campaignDirectoryEmptyText = computed(() => {
  if (isLoadingCampaigns.value) return ''
  if (!availableCampaigns.value.length) return 'No hay campañas disponibles para transferir.'
  return 'No encontramos campañas con la búsqueda actual.'
})

const botDirectoryEmptyText = computed(() => {
  if (isLoadingBots.value) return ''
  if (!availableBots.value.length) return 'No hay bots disponibles para transferir.'
  return 'No encontramos bots con la búsqueda actual.'
})

const showSummary = computed(() => {
  if (transferType.value === 'campaign' && selectedCampaign.value) return true
  if (transferType.value === 'user' && selectedUser.value) return true
  if (transferType.value === 'bot' && selectedBot.value) return true
  return false
})

const showPendingAgentTransfer = computed(() => {
  return !!props.isAwaitingTransferResponse && transferType.value === 'user' && !!selectedUser.value
})

const showFooterActions = computed(() => {
  return showSummary.value && !showPendingAgentTransfer.value
})

const summaryDestinationName = computed(() => {
  if (transferType.value === 'campaign' && selectedCampaign.value) {
    return selectedCampaign.value.name
  }
  if (transferType.value === 'user' && selectedUser.value) {
    return selectedUser.value.name
  }
  if (transferType.value === 'bot' && selectedBot.value) {
    return selectedBot.value.name
  }
  return ''
})

const summaryDestinationSubtitle = computed(() => {
  if (transferType.value === 'campaign' && selectedCampaign.value) {
    return getCampaignTypeLabel(selectedCampaign.value.type)
  }
  if (transferType.value === 'user' && selectedUser.value) {
    return selectedUser.value.campaignName
  }
  if (transferType.value === 'bot' && selectedBot.value?.description) {
    return selectedBot.value.description
  }
  return null
})

const summaryDestinationMeta = computed(() => {
  if (transferType.value === 'bot' && selectedBot.value) {
    return `Bot ID: ${selectedBot.value.id}`
  }
  return null
})

const summaryTransferType = computed<'campaign' | 'user' | 'bot'>(() => {
  return transferType.value ?? 'campaign'
})

const canTransfer = computed(() => {
  if (transferType.value === 'campaign' && selectedCampaign.value) return true
  if (transferType.value === 'user' && selectedUser.value) return true
  if (transferType.value === 'bot' && selectedBot.value) return true
  return false
})

const resetTransferFormState = (options?: { clearLoadedData?: boolean }) => {
  stopTransferCountdown()
  transferResponseCountdown.value = 20
  transferType.value = null
  selectedCampaign.value = null
  selectedUserCampaign.value = null
  selectedUser.value = null
  selectedBot.value = null
  transferMessage.value = ''
  userSearchTerm.value = ''
  campaignSearchTerm.value = ''
  botSearchTerm.value = ''

  if (options?.clearLoadedData) {
    transferUsersDirectory.value = []
    resetTransferData()
  }
}

const stopTransferCountdown = () => {
  if (transferResponseCountdownInterval) {
    clearInterval(transferResponseCountdownInterval)
    transferResponseCountdownInterval = null
  }
}

const startTransferCountdown = () => {
  stopTransferCountdown()
  transferResponseCountdown.value = 20
  transferResponseCountdownInterval = setInterval(() => {
    if (transferResponseCountdown.value <= 0) {
      stopTransferCountdown()
      return
    }

    transferResponseCountdown.value -= 1
  }, 1000)
}

const handleTransferTypeChange = (nextType: 'campaign' | 'user' | 'bot') => {
  if (nextType === 'bot' && availableBots.value.length === 0) {
    isLoadingBots.value = true
  }

  transferType.value = nextType
}

const handleBackStep = () => {
  if (showSummary.value) {
    if (transferType.value === 'campaign') {
      selectedCampaign.value = null
      return
    }

    if (transferType.value === 'user') {
      selectedUser.value = null
      return
    }

    if (transferType.value === 'bot') {
      selectedBot.value = null
      return
    }
  }

  transferType.value = null
  selectedCampaign.value = null
  selectedUserCampaign.value = null
  selectedUser.value = null
  selectedBot.value = null
  transferMessage.value = ''
  userSearchTerm.value = ''
  campaignSearchTerm.value = ''
  botSearchTerm.value = ''
}

const handleTransfer = async () => {
  if (!canTransfer.value || !transferType.value) {
    loadError.value = 'Debes completar la información de la transferencia.'
    return
  }

  isLoading.value = true

  try {
    let transferData: TransferPayload | null = null

    if (transferType.value === 'campaign' && selectedCampaign.value) {
      transferData = {
        chatId: props.chatId,
        transferType: 'campaign',
        toCampaignId: selectedCampaign.value.id,
        toCampaignName: selectedCampaign.value.name,
      }
    } else if (transferType.value === 'user' && selectedUser.value) {
      transferData = {
        chatId: props.chatId,
        transferType: 'user',
        toUserId: selectedUser.value.id,
        toUserName: selectedUser.value.name,
        toCampaignId: selectedUser.value.campaignId,
        toCampaignName: selectedUser.value.campaignName,
        ...(transferMessage.value.trim() ? { transfer_message: transferMessage.value.trim() } : {}),
      }
    } else if (transferType.value === 'bot' && selectedBot.value) {
      transferData = {
        chatId: props.chatId,
        transferType: 'bot',
        toBotId: selectedBot.value.id,
        toBotName: selectedBot.value.name,
      }
    }

    if (!transferData) {
      loadError.value = 'No se pudo construir la transferencia.'
      return
    }

    emit('transfer', transferData)

    if (transferData.transferType !== 'user') {
      handleClose()
    }
  } finally {
    isLoading.value = false
  }
}

const handleClose = () => {
  if (isClosingDisabled.value) return

  resetTransferFormState({ clearLoadedData: true })

  emit('update:visible', false)
}

const closeWaiting = () => {
  if (isClosingDisabled.value) return

  resetTransferFormState({ clearLoadedData: true })

  emit('update:visible', false)
}

const applyRestoreDraft = async () => {
  const restoreDraft = props.restoreDraft

  if (!restoreDraft || restoreDraft.nonce === lastAppliedRestoreNonce.value) {
    return
  }

  lastAppliedRestoreNonce.value = restoreDraft.nonce
  resetTransferFormState()

  if (restoreDraft.action === 'type') {
    return
  }

  const transferData = restoreDraft.transferData
  if (!transferData || transferData.transferType !== 'user') {
    return
  }

  isRestoringDraft.value = true
  transferType.value = 'user'
  selectedUserCampaign.value =
    availableCampaigns.value.find((campaign) => campaign.id === transferData.toCampaignId) ?? null
  selectedUser.value = transferUsersDirectory.value.find(
    (user) => user.id === transferData.toUserId && user.campaignId === transferData.toCampaignId,
  ) ?? {
    id: transferData.toUserId,
    name: transferData.toUserName,
    campaignId: transferData.toCampaignId,
    campaignName: transferData.toCampaignName,
  }
  transferMessage.value = transferData.transfer_message ?? ''

  await nextTick()
  isRestoringDraft.value = false

  await handleTransfer()
}

const loadBotsIfNeeded = async () => {
  if (availableBots.value.length === 0) {
    isLoadingBots.value = true
    try {
      loadError.value = null
      const bots = await botService.getBotOptions(chatChannel.value)
      availableBots.value = bots
    } catch {
      loadError.value = 'Error al cargar la lista de bots'
    } finally {
      isLoadingBots.value = false
    }
  }
}

// El diálogo sigue montado al cambiar de chat: con otro canal, otros bots.
watch(
  () => props.channelId,
  () => {
    availableBots.value = []
  }
)

const loadTransferUsersDirectory = async () => {
  if (!availableCampaigns.value.length) {
    transferUsersDirectory.value = []
    return
  }

  isLoadingUserDirectory.value = true

  try {
    loadError.value = null
    const campaignUsers = await Promise.all(
      availableCampaigns.value.map(async (campaign) => {
        const users = await campaignService.getCampaignUsers(campaign.id)

        return users
          .filter((user) => user.connected === true && user.id !== props.currentUserId)
          .map((user) => ({
            id: user.id,
            name: user.name,
            email: user.email,
            avatar: user.avatar,
            alias: user.alias,
            campaignId: campaign.id,
            campaignName: campaign.name,
          }))
      }),
    )

    transferUsersDirectory.value = campaignUsers.flat()
  } catch {
    transferUsersDirectory.value = []
    loadError.value = 'Error al cargar los agentes de las campañas'
  } finally {
    isLoadingUserDirectory.value = false
  }
}

const getCampaignTypeLabel = (type?: CampaignOption['type']): string => {
  if (!type) return ''

  const labels: Record<NonNullable<CampaignOption['type']>, string> = {
    BLASTING: 'Blasting',
    INCOMING: 'Entrante',
    OUTGOING: 'Saliente',
    CHAT: 'Chat',
    PREVIEW: 'Preview',
  }

  return labels[type] || type
}

watch(transferType, () => {
  if (!isRestoringDraft.value) {
    selectedCampaign.value = null
    selectedUserCampaign.value = null
    selectedUser.value = null
    selectedBot.value = null
    transferMessage.value = ''
    userSearchTerm.value = ''
    campaignSearchTerm.value = ''
    botSearchTerm.value = ''
  }

  if (transferType.value === 'bot') {
    void loadBotsIfNeeded()
  }

})

watch(selectedUserCampaign, () => {
  if (isRestoringDraft.value) return

  selectedUser.value = null
  transferMessage.value = ''
})

watch(showPendingAgentTransfer, (isPending) => {
  if (isPending) {
    startTransferCountdown()
    return
  }

  stopTransferCountdown()
  transferResponseCountdown.value = 20
})

watch(
  () => props.visible,
  async (visible) => {
    if (visible) {
      await loadWhatsAppCampaigns(props.currentUserId, chatChannel.value)
      await Promise.all([loadTransferUsersDirectory(), loadBotsIfNeeded()])
      await applyRestoreDraft()
    } else {
      handleClose()
    }
  },
)

onBeforeUnmount(() => {
  stopTransferCountdown()
})
</script>
